var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * EventBar Component
 * Renders a horizontal event bar that can span multiple days
 */
import React, { useCallback, useMemo, memo } from 'react';
import { generateEventAriaLabel } from '../../utils/accessibilityUtils';
import { getEventBarTop, getGeometry } from '../../utils/eventLayout';
import { isEventClickable } from '../../utils/eventUtils';
import { styles } from './EventBar.styles';
/**
 * EventBar - Horizontal bar representing an event
 */
export const EventBar = memo(({ event, spanDays = 1, order = 1, isStart = true, isEnd = true, isClickable: isClickableProp, showName = true, showInitial = true, showTime = false, compact = false, onEventClick, onEventHover, className, renderContent, }) => {
    // Determine if event is clickable
    const clickable = useMemo(() => {
        if (isClickableProp !== undefined)
            return isClickableProp;
        return isEventClickable(event);
    }, [event, isClickableProp]);
    // Generate ARIA label
    const ariaLabel = useMemo(() => {
        return generateEventAriaLabel(event);
    }, [event]);
    // Build class names
    const barClassName = useMemo(() => {
        const classes = ['event-bar'];
        if (isStart)
            classes.push('event-bar-start');
        if (isEnd)
            classes.push('event-bar-end');
        if (!clickable)
            classes.push('event-bar-readonly');
        if (compact)
            classes.push('event-bar-compact');
        if (event.isPublicHoliday)
            classes.push('event-bar-holiday');
        if (event.highlightBorder)
            classes.push('event-bar-highlight');
        if (className)
            classes.push(className);
        return classes.join(' ');
    }, [isStart, isEnd, clickable, compact, event.isPublicHoliday, event.highlightBorder, className]);
    // Calculate bar style
    const barStyle = useMemo(() => {
        const baseStyle = Object.assign(Object.assign({}, styles.bar), { backgroundColor: event.bgcolor || '#0078d4', color: event.color || '#ffffff', width: spanDays > 1 ? `calc(${spanDays * 100}% + ${(spanDays - 1) * 2}px)` : '100%', top: `${getEventBarTop(order, compact)}px`, height: `${getGeometry(compact).barHeight}px`, cursor: clickable ? 'pointer' : 'default' });
        // Apply rounded corners based on position
        if (isStart && isEnd) {
            baseStyle.borderRadius = '3px';
        }
        else if (isStart) {
            baseStyle.borderRadius = '3px 0 0 3px';
        }
        else if (isEnd) {
            baseStyle.borderRadius = '0 3px 3px 0';
        }
        else {
            baseStyle.borderRadius = '0';
        }
        // Apply highlight border
        if (event.highlightBorder && event.highlightBorderColor) {
            baseStyle.borderLeft = `3px solid ${event.highlightBorderColor}`;
        }
        return baseStyle;
    }, [event, spanDays, order, isStart, isEnd, compact, clickable]);
    // Handle click
    const handleClick = useCallback((e) => {
        if (!clickable)
            return;
        e.stopPropagation();
        onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick(event, e);
    }, [event, clickable, onEventClick]);
    // Handle hover
    const handleMouseEnter = useCallback((e) => {
        onEventHover === null || onEventHover === void 0 ? void 0 : onEventHover(event, e);
    }, [event, onEventHover]);
    // Handle keyboard
    const handleKeyDown = useCallback((e) => {
        if (!clickable)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick(event, e);
        }
    }, [event, clickable, onEventClick]);
    return (React.createElement("div", { className: barClassName, style: barStyle, role: "button", "aria-label": ariaLabel, tabIndex: clickable ? 0 : -1, onClick: handleClick, onMouseEnter: handleMouseEnter, onKeyDown: handleKeyDown, "data-event-id": event.id, "data-order": order }, renderContent ? (renderContent(event)) : (React.createElement("div", { style: styles.content },
        showInitial && event.initial && (React.createElement("span", { style: styles.initial, "aria-hidden": "true" }, event.initial)),
        event.isLostLabor && event.lostLaborIcon && (React.createElement("span", { className: event.lostLaborIcon, style: styles.icon, "aria-label": "Lost labor" })),
        showName && (React.createElement("span", { style: styles.name, title: event.name }, event.name)),
        showTime && event.timeMetaData && (React.createElement("span", { style: styles.time }, event.timeMetaData)),
        event.isPublicHoliday && (React.createElement("span", { style: styles.holidayBadge, "aria-hidden": "true" }, "\uD83C\uDFD6"))))));
});
EventBar.displayName = 'EventBar';
export default EventBar;
//# sourceMappingURL=EventBar.js.map